/**
 * ChickenActor — the hero. Owns its own little animation state machine;
 * the Game object never knows about pixels.
 *
 * All transforms anchor at the feet (bottom-center) so squash & stretch
 * reads as weight, not as floating scaling.
 */
import { MANHOLE_Y, CHICKEN_START_X } from './Layout.js';

const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - (1 - t) * (1 - t) * 2);

const ANTICIPATION = 0.14; // fraction of the hop spent crouching before liftoff

export default class ChickenActor {
  constructor(sprites) {
    this.sprites = sprites;
    this.mode = 'idle'; // idle | hop | dead | celebrate
    this.worldX = CHICKEN_START_X;
    this.footY = MANHOLE_Y + 44;
    this.t = 0;
    this.time = 0;
    this.blinkAt = 2.5;
    this.blinking = 0;
    this.landSquash = 0; // >0 while recovering from a landing
    this.hopMeta = null;
  }

  reset() {
    this.mode = 'idle';
    this.worldX = CHICKEN_START_X;
    this.hopMeta = null;
    this.landSquash = 0;
  }

  hopTo(targetX, durationMs, onLand) {
    this.mode = 'hop';
    this.t = 0;
    this.hopMeta = { fromX: this.worldX, toX: targetX, dur: durationMs / 1000, onLand };
  }

  die() {
    this.mode = 'dead';
  }

  celebrate() {
    this.mode = 'celebrate';
    this.t = 0;
  }

  /** Height above the ground right now (used for the shadow + dust). */
  get airT() {
    if (this.mode !== 'hop') return 0;
    const jump = Math.max(0, (this.t - ANTICIPATION) / (1 - ANTICIPATION));
    return Math.sin(Math.PI * Math.min(1, jump));
  }

  update(dt) {
    this.time += dt;
    if (this.landSquash > 0) this.landSquash = Math.max(0, this.landSquash - dt / 0.16);

    if (this.mode === 'idle') {
      this.blinkAt -= dt;
      if (this.blinkAt <= 0) {
        this.blinking = 0.13;
        this.blinkAt = 2.2 + Math.random() * 2.2;
      }
      if (this.blinking > 0) this.blinking -= dt;
    } else if (this.mode === 'hop') {
      const m = this.hopMeta;
      this.t = Math.min(1, this.t + dt / m.dur);
      const jump = Math.max(0, (this.t - ANTICIPATION) / (1 - ANTICIPATION));
      this.worldX = m.fromX + (m.toX - m.fromX) * easeInOut(jump);
      if (this.t >= 1) {
        this.worldX = m.toX;
        this.mode = 'idle';
        this.landSquash = 1;
        const onLand = m.onLand;
        this.hopMeta = null;
        onLand?.();
      }
    } else if (this.mode === 'celebrate') {
      this.t += dt;
    }
  }

  draw(ctx, camX) {
    const x = this.worldX - camX;
    const s = this.sprites;

    if (this.mode === 'dead') {
      const spr = s.chickenDead;
      ctx.drawImage(spr, x - spr.logicalW / 2, this.footY - spr.logicalH + 6, spr.logicalW, spr.logicalH);
      return;
    }

    let spr = s.chicken;
    let hopY = 0;
    let sx = 1, sy = 1;

    if (this.mode === 'hop') {
      const air = this.airT;
      hopY = -92 * air;
      if (this.t < ANTICIPATION) {
        const p = this.t / ANTICIPATION; // crouch
        sx = 1 + 0.13 * p;
        sy = 1 - 0.18 * p;
      } else {
        spr = s.chickenHop;
        sx = 0.94;
        sy = 1.10;
      }
    } else {
      if (this.mode === 'celebrate') {
        const bounce = Math.abs(Math.sin(this.t * 7));
        hopY = -20 * bounce;
        if (Math.sin(this.t * 7) > 0) spr = s.chickenHop;
      } else {
        hopY = Math.sin(this.time * 3.4) * 2.4; // idle bob
        if (this.blinking > 0) spr = s.chickenBlink;
      }
      if (this.landSquash > 0) {
        const q = this.landSquash * this.landSquash;
        sx = 1 + 0.16 * q;
        sy = 1 - 0.2 * q;
      }
    }

    // ground shadow — stays on the ground, shrinks while airborne
    const air = this.airT + (this.mode === 'celebrate' ? -hopY / 92 : 0);
    const shScale = 1 - 0.32 * air;
    ctx.fillStyle = `rgba(18,20,26,${0.28 * (1 - 0.45 * air)})`;
    ctx.beginPath();
    ctx.ellipse(x, this.footY + 2, 46 * shScale, 13 * shScale, 0, 0, Math.PI * 2);
    ctx.fill();

    // sprite, anchored at the feet
    ctx.save();
    ctx.translate(x, this.footY + hopY);
    ctx.scale(sx, sy);
    ctx.drawImage(spr, -spr.logicalW / 2, -spr.logicalH + 8, spr.logicalW, spr.logicalH);
    ctx.restore();
  }
}
